import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

import { PAGE_CONTENT_CLASS } from "components/layout/PageContent";
import PageHeader from "components/PageHeader";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import LoadingMark from "components/ui/LoadingMark";
import { ModalHeader, ModalShell } from "components/ui/ModalFrame";
import { DEFAULT_CRM_CATEGORY_ID } from "constants/crm";
import crmService from "services/crmService";
import TemplateFormModal from "./TemplateFormModal";

const CrmRespostasPage = () => {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categoryError, setCategoryError] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setListError(null);
    const [catRes, tplRes] = await Promise.all([
      crmService.listCategories(),
      crmService.listTemplates(),
    ]);

    if (!catRes.success || !tplRes.success) {
      setListError(
        (typeof catRes.error === "string" && catRes.error) ||
          (typeof tplRes.error === "string" && tplRes.error) ||
          "Erro ao carregar respostas"
      );
      setCategories([]);
      setTemplates([]);
      setLoading(false);
      return;
    }

    setCategories(catRes.data || []);
    setTemplates(tplRes.data || []);
    setExpandedCategories((prev) => {
      const next = { ...prev };
      for (const c of catRes.data || []) {
        if (next[c.id] === undefined) next[c.id] = true;
      }
      return next;
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredTemplates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.body || "").toLowerCase().includes(q) ||
        (t.listing_label || "").toLowerCase().includes(q)
    );
  }, [templates, query]);

  const grouped = useMemo(() => {
    return categories.map((category) => {
      const items = filteredTemplates
        .filter((t) => (t.category_id || DEFAULT_CRM_CATEGORY_ID) === category.id)
        .sort((a, b) => {
          if (Boolean(a.is_favorite) !== Boolean(b.is_favorite)) {
            return a.is_favorite ? -1 : 1;
          }
          return (a.title || "").localeCompare(b.title || "", "pt-BR");
        });
      return { category, items };
    });
  }, [categories, filteredTemplates]);

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateModalOpen(true);
  };

  const handleSaveTemplate = async (payload) => {
    setSaving(true);
    try {
      const result = await crmService.saveTemplate(payload);
      if (!result.success) {
        throw new Error(
          typeof result.error === "string"
            ? result.error
            : result.error?.message || "Erro ao salvar resposta"
        );
      }
      toast.success(payload.id ? "Resposta atualizada." : "Resposta criada.");
      setTemplateModalOpen(false);
      await loadAll();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFavorite = async (template) => {
    const result = await crmService.saveTemplate({
      id: template.id,
      category_id: template.category_id,
      title: template.title,
      body: template.body,
      listing_label: template.listing_label,
      link_url: template.link_url,
      is_favorite: !template.is_favorite,
    });
    if (!result.success) {
      toast.error(
        typeof result.error === "string"
          ? result.error
          : "Erro ao favoritar"
      );
      return;
    }
    await loadAll();
  };

  const handleDeleteTemplate = async (template) => {
    const ok = window.confirm(`Excluir a resposta "${template.title}"?`);
    if (!ok) return;
    const result = await crmService.deleteTemplate(template.id);
    if (!result.success) {
      toast.error(
        typeof result.error === "string"
          ? result.error
          : "Erro ao excluir resposta"
      );
      return;
    }
    toast.success("Resposta excluída.");
    await loadAll();
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setCategoryError(null);
    if (!categoryName.trim()) {
      setCategoryError("Nome é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      const result = await crmService.saveCategory({
        name: categoryName.trim(),
      });
      if (!result.success) {
        setCategoryError(
          typeof result.error === "string"
            ? result.error
            : "Erro ao salvar categoria"
        );
        return;
      }
      toast.success("Categoria criada.");
      setCategoryModalOpen(false);
      setCategoryName("");
      await loadAll();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (category.id === DEFAULT_CRM_CATEGORY_ID) return;
    const ok = window.confirm(
      `Excluir a categoria "${category.name}"? As respostas vão para "Geral".`
    );
    if (!ok) return;
    const result = await crmService.deleteCategory(category.id);
    if (!result.success) {
      toast.error(
        typeof result.error === "string"
          ? result.error
          : "Erro ao excluir categoria"
      );
      return;
    }
    toast.success("Categoria excluída.");
    await loadAll();
  };

  const handleCopy = async (template) => {
    try {
      await navigator.clipboard.writeText(template.body || "");
      toast.success("Texto copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <>
      <div className={PAGE_CONTENT_CLASS}>
        <Helmet>
          <title>Respostas Padrão — CRM Neo Documentos</title>
        </Helmet>

        <PageHeader
          title="Respostas Padrão"
          subtitle="Mesmas respostas da extensão WhatsApp — edite com mais espaço no desktop"
          icon="MessageSquareText"
          action={
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => {
                  setCategoryName("");
                  setCategoryError(null);
                  setCategoryModalOpen(true);
                }}
              >
                <Icon name="FolderPlus" size={18} />
                Nova categoria
              </Button>
              <Button type="button" className="gap-2" onClick={openNewTemplate}>
                <Icon name="Plus" size={18} />
                Nova resposta
              </Button>
            </div>
          }
        />

        <div className="mb-4 w-full sm:max-w-sm">
          <Input
            placeholder="Buscar respostas…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading && (
          <div className="flex justify-center py-10">
            <LoadingMark message="Carregando respostas…" />
          </div>
        )}

        {!loading && listError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {listError}
          </div>
        )}

        {!loading && !listError && templates.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
            Nenhuma resposta cadastrada. Crie aqui ou na extensão do WhatsApp.
          </div>
        )}

        {!loading && !listError && (
          <div className="space-y-4">
            {grouped.map(({ category, items }) => {
              if (query.trim() && items.length === 0) return null;
              const open = expandedCategories[category.id] !== false;
              return (
                <section
                  key={category.id}
                  className="rounded-xl border border-border bg-card overflow-hidden"
                >
                  <div className="flex items-center gap-2 border-b border-border bg-muted/20 px-4 py-3">
                    <button
                      type="button"
                      className="flex flex-1 items-center gap-2 text-left min-w-0"
                      onClick={() =>
                        setExpandedCategories((prev) => ({
                          ...prev,
                          [category.id]: !open,
                        }))
                      }
                      aria-expanded={open}
                    >
                      <Icon
                        name={open ? "ChevronDown" : "ChevronRight"}
                        size={18}
                        className="shrink-0 text-muted-foreground"
                      />
                      <span className="font-semibold truncate">{category.name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {items.length}
                      </span>
                    </button>
                    {category.id !== DEFAULT_CRM_CATEGORY_ID && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        title="Excluir categoria"
                        onClick={() => handleDeleteCategory(category)}
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    )}
                  </div>

                  {open && (
                    <ul className="divide-y divide-border">
                      {items.length === 0 && (
                        <li className="px-4 py-6 text-sm text-muted-foreground text-center">
                          Nenhuma resposta nesta categoria.
                        </li>
                      )}
                      {items.map((template) => (
                        <li
                          key={template.id}
                          className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground truncate">
                                {template.title}
                              </p>
                              {template.is_favorite && (
                                <Icon
                                  name="Star"
                                  size={14}
                                  className="shrink-0 text-amber-500"
                                />
                              )}
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                              {template.body}
                            </p>
                            {template.listing_label && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                Empreendimento: {template.listing_label}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9"
                              title={
                                template.is_favorite
                                  ? "Remover dos favoritos"
                                  : "Favoritar"
                              }
                              onClick={() => handleToggleFavorite(template)}
                            >
                              <Icon
                                name="Star"
                                size={18}
                                className={
                                  template.is_favorite
                                    ? "text-amber-500 fill-amber-500"
                                    : undefined
                                }
                              />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9"
                              title="Copiar texto"
                              onClick={() => handleCopy(template)}
                            >
                              <Icon name="Copy" size={18} />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9"
                              title="Editar"
                              onClick={() => openEditTemplate(template)}
                            >
                              <Icon name="Pencil" size={18} />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-destructive hover:text-destructive"
                              title="Excluir"
                              onClick={() => handleDeleteTemplate(template)}
                            >
                              <Icon name="Trash2" size={18} />
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <TemplateFormModal
        open={templateModalOpen}
        template={editingTemplate}
        categories={categories}
        saving={saving}
        onClose={() => setTemplateModalOpen(false)}
        onSave={handleSaveTemplate}
      />

      {categoryModalOpen && (
        <ModalShell>
          <ModalHeader
            title="Nova categoria"
            onClose={() => setCategoryModalOpen(false)}
          />
          <form onSubmit={handleSaveCategory} className="space-y-4 px-5 py-4">
            {categoryError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {categoryError}
              </div>
            )}
            <Input
              label="Nome"
              required
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Ex.: Visitas"
            />
            <div className="flex justify-end gap-2 pb-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCategoryModalOpen(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Salvando…" : "Salvar"}
              </Button>
            </div>
          </form>
        </ModalShell>
      )}
    </>
  );
};

export default CrmRespostasPage;
